import { useCallback, useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Card, Btn, ModeBadge } from "@/components/kit";
import { Instagram, Facebook, Youtube, MessageCircle, MapPin, Plug, Music2 } from "lucide-react";

const ICONS = {
  "Instagram": Instagram, "Facebook": Facebook, "YouTube": Youtube, "YouTube Shorts": Youtube,
  "WhatsApp": MessageCircle, "Google Business": MapPin, "TikTok": Music2,
};

export default function Conexoes() {
  const { activeOrgId } = useAuth();
  const [data, setData] = useState({ active: [], future: [] });
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");

  const load = useCallback(() => {
    if (!activeOrgId) return;
    client.get("/connections", { params: brandId ? { brand_id: brandId } : {} }).then((r) => setData(r.data));
  }, [activeOrgId, brandId]);

  useEffect(() => {
    if (!activeOrgId) return;
    client.get("/brands").then((r) => {
      setBrands(r.data);
      setBrandId((prev) => prev || (r.data[0] ? r.data[0].id : ""));
    });
  }, [activeOrgId]);

  useEffect(load, [load]);

  // Recarrega quando a janela volta ao foco (após concluir o seletor de Página no popup do Meta).
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const activeBrandName = (brands.find((b) => b.id === brandId) || {}).name || "";

  const configure = async (channel) => {
    try { const { data: r } = await client.post(`/connections/${encodeURIComponent(channel)}/configure`); toast.info(r.message); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const connectOAuth = async (channel, perBrand) => {
    if (perBrand && !brandId) { toast.warning("Selecione uma marca antes de conectar o Instagram/Facebook."); return; }
    try {
      const params = perBrand ? { brand_id: brandId } : {};
      const { data } = await client.get(`/connections/${encodeURIComponent(channel)}/oauth/start`, { params });
      if (data.configured && data.authorize_url) {
        window.open(data.authorize_url, "_blank", "noopener,width=520,height=680");
        toast.success(perBrand
          ? `Faça login no Meta e escolha a Página da marca "${activeBrandName}".`
          : `Redirecionando para autorização (${data.provider}).`);
      } else {
        toast.warning(data.message, { duration: 8000 });
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Central de Conexões" subtitle="Integrações via OAuth e APIs oficiais. Nada é marcado como conectado sem confirmação da API oficial." />

      <div className="rounded-xl bg-sky-50 border border-sky-100 p-4 text-sm text-sky-700 mb-4">
        <b>Ambiente seguro.</b> Não usamos scraping, automação de navegador nem senhas das redes. Instagram e Facebook são conectados <b>por marca</b> — cada marca tem sua Página/Instagram próprios.
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <label className="text-sm font-medium text-slate-600">Conexões da marca:</label>
        <select
          data-testid="conn-brand-select"
          value={brandId}
          onChange={(e) => setBrandId(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        >
          {brands.length === 0 && <option value="">(nenhuma marca)</option>}
          {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
        </select>
        <span className="text-xs text-slate-400">Instagram/Facebook mostram a conexão desta marca.</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.active.map((c) => {
          const Icon = ICONS[c.channel] || Plug;
          return (
            <Card key={c.channel} data-testid={`connection-${c.channel}`} className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700"><Icon size={20} /></div><div><div className="font-semibold text-slate-800">{c.channel}</div>{c.connected_as && <div className="text-[11px] text-emerald-600 font-medium" data-testid={`connected-as-${c.channel}`}>{c.connected_as}</div>}</div></div>
                <ModeBadge mode={c.status === "CONNECTED" ? "CONNECTED" : "SANDBOX"} glow />
              </div>
              {c.per_brand && (
                <div className="text-[11px] font-medium text-indigo-600 bg-indigo-50 rounded-md px-2 py-1 w-fit">
                  Marca: {activeBrandName || "—"}
                </div>
              )}
              <p className="text-xs text-slate-500 flex-1">{c.note}</p>
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className={`h-2 w-2 rounded-full ${c.oauth_configured ? "bg-emerald-500" : "bg-slate-300"}`} />
                <span className="text-slate-500">Credenciais {c.provider}: {c.oauth_configured ? "configuradas" : "pendentes"}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Btn variant="primary" className="w-full !px-2 text-xs" data-testid={`oauth-${c.channel}`} disabled={c.per_brand && !brandId} onClick={() => connectOAuth(c.channel, c.per_brand)}>
                  {c.status === "CONNECTED" ? "Reconectar" : "Conectar OAuth"}
                </Btn>
                <Btn variant="secondary" className="w-full !px-2 text-xs" data-testid={`configure-${c.channel}`} onClick={() => configure(c.channel)}>Sandbox</Btn>
              </div>
              {c.docs && <a href={c.docs} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#3457d5] hover:underline">Ver documentação do app →</a>}
            </Card>
          );
        })}
      </div>

      <h3 className="font-display font-bold text-slate-700 mt-8 mb-3">Connectors planejados</h3>
      <div className="flex flex-wrap gap-2">
        {data.future.map((c) => (
          <span key={c.channel} data-testid={`future-${c.channel}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-500">
            <Plug size={14} /> {c.channel} <span className="text-[10px] text-slate-400">· planejado</span>
          </span>
        ))}
      </div>
    </div>
  );
}
